/**
 * PdfExportService
 * Handles PDF export functionality for datatables
 * Uses jsPDF and jspdf-autotable for formatted PDF reports
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { ExportColumn } from './ExportService';

class PdfExportService {
    /**
     * Export data to PDF format
     * @param data - Array of data objects to export
     * @param columns - Array of column definitions
     * @param filename - Name of the file to download
     * @param title - Title for the PDF document
     * @param filters - Active filters to display in the PDF
     */
    exportToPDF(
        data: any[], 
        columns: ExportColumn[], 
        filename: string = 'export.pdf',
        title: string = 'Data Export',
        filters?: Record<string, any>
    ): void {
        // Filter to only selected columns
        const selectedColumns = columns.filter(col => col.selected);
        
        if (selectedColumns.length === 0) {
            throw new Error('No columns selected for export');
        }

        // Create new PDF document
        const doc = new jsPDF({
            orientation: selectedColumns.length > 6 ? 'landscape' : 'portrait',
            unit: 'mm',
            format: 'a4'
        });

        // Add title
        doc.setFontSize(16);
        doc.text(title, 14, 15);

        let currentY = 25;

        // Add filter information if provided
        if (filters && Object.keys(filters).length > 0) {
            doc.setFontSize(10);
            doc.text('Applied Filters:', 14, currentY);
            currentY += 5;

            const filterEntries = Object.entries(filters)
                .filter(([_, value]) => value && (Array.isArray(value) ? value.length > 0 : true));

            filterEntries.forEach(([key, value]) => {
                const label = this.formatFilterLabel(key);
                const valueStr = Array.isArray(value) 
                    ? value.join(', ') 
                    : String(value);
                
                doc.setFontSize(9);
                doc.text(`${label}: ${valueStr}`, 14, currentY);
                currentY += 4;
            });

            currentY += 5;
        }

        // Prepare table data
        const headers = selectedColumns.map(col => col.label);
        const rows = data.map(row => 
            selectedColumns.map(col => {
                const value = this.getNestedValue(row, col.name);
                return this.formatValue(value);
            })
        );

        // Generate table
        autoTable(doc, {
            head: [headers],
            body: rows,
            startY: currentY,
            styles: {
                fontSize: 8,
                cellPadding: 2,
            },
            headStyles: {
                fillColor: [41, 128, 185],
                textColor: 255,
                fontStyle: 'bold'
            },
            alternateRowStyles: {
                fillColor: [245, 245, 245]
            },
            margin: { top: currentY, left: 14, right: 14 },
            theme: 'striped',
            didDrawPage: (data) => {
                // Footer with page numbers
                const pageCount = (doc as any).internal.getNumberOfPages();
                doc.setFontSize(8);
                doc.text(
                    `Page ${data.pageNumber} of ${pageCount}`,
                    doc.internal.pageSize.getWidth() / 2,
                    doc.internal.pageSize.getHeight() - 10,
                    { align: 'center' }
                );

                // Add timestamp
                doc.text(
                    `Generated: ${new Date().toLocaleString()}`,
                    14,
                    doc.internal.pageSize.getHeight() - 10
                );
            }
        });

        // Save the PDF
        doc.save(filename);
    }

    /**
     * Format value for PDF display
     * @param value - Value to format
     */
    private formatValue(value: any): string {
        if (value === null || value === undefined) {
            return '';
        }

        // Handle dates
        if (value instanceof Date) {
            return value.toLocaleDateString();
        }

        // Handle objects (show as JSON)
        if (typeof value === 'object') {
            return JSON.stringify(value);
        }

        // Handle booleans
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }

        return String(value);
    }

    /**
     * Get nested object value by string path
     * @param obj - Object to search
     * @param path - Dot-notation path
     */
    private getNestedValue(obj: any, path: string): any {
        return path.split('.').reduce((current, prop) => {
            return current?.[prop];
        }, obj);
    }

    /**
     * Format filter key to human-readable label
     * @param key - Filter key
     */
    private formatFilterLabel(key: string): string {
        return key
            .replace(/([A-Z])/g, ' $1')
            .replace(/_/g, ' ')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    }
}

const pdfExportService = new PdfExportService();
export default pdfExportService;
